// src/components/superadmin/Sidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineUsers,
  HiOutlineClock,
  HiOutlineCog,
  HiOutlineLogout,
  HiMenu,
  HiX,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineChartBar,
  HiOutlineAcademicCap,
  HiOutlineUserGroup,
  HiCalendar 
} from 'react-icons/hi';
import { superAdminService } from '../.././superAdminService.js';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('superAdminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  
  const sidebarRef = useRef(null);
  const location = useLocation();

  // Debug log to see if props are received
  console.log('Sidebar props:', { isOpen, toggleSidebar });

  // Save collapse preference
  useEffect(() => {
    localStorage.setItem('superAdminSidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scroll for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      if (sidebarRef.current) {
        setScrolled(sidebarRef.current.scrollTop > 10);
      }
    };
    
    const currentRef = sidebarRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
      return () => currentRef.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt + B to toggle sidebar collapse
      if (e.altKey && e.key === 'b' && !isMobile) {
        e.preventDefault();
        setIsCollapsed(!isCollapsed);
      }
      
      // Alt + 1-4 for menu items
      if (e.altKey && !isNaN(parseInt(e.key)) && !isMobile) {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (menuItems[index]) {
          window.location.href = menuItems[index].path;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCollapsed, isMobile]);

  const menuItems = [
    { path: '/dashboard', icon: HiOutlineHome, label: 'Dashboard', shortcut: '1' },
    { path: '/super-admin/pending', icon: HiOutlineClock, label: 'Pending Approvals', shortcut: '2' },
    { path: '/super-admin/colleges', icon: HiOutlineOfficeBuilding, label: 'Colleges', shortcut: '3' },
    { path: '/super-admin/users', icon: HiOutlineUsers, label: 'Users', shortcut: '4' },
    { path: '/super-admin/courses', icon: HiOutlineAcademicCap, label: 'Courses', shortcut: '5' },
    // { path: '/super-admin/events', icon: HiCalendar , label: 'Events', shortcut: '6' },
  ];

 

  const handleLogout = () => {
    toast.promise(
      new Promise((resolve) => {
        setTimeout(() => {
          Cookies.remove('superAdminToken');
          localStorage.removeItem('superAdminEmail');
          localStorage.removeItem('superAdminId');
          resolve();
        }, 1000);
      }),
      {
        loading: 'Logging out...',
        success: 'Logged out successfully!',
        error: 'Failed to logout',
      }
    ).then(() => {
      window.location.href = '/login';
    });
  };

  const getActiveItem = () => {
    return menuItems.find(item => item.path === location.pathname)?.label || '';
  };

  // Tooltip component for collapsed mode
  const Tooltip = ({ children, label, shortcut }) => (
    <div className="relative group">
      {children}
      {isCollapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-lg">
          {label}
          {shortcut && <span className="ml-2 text-gray-400">Alt+{shortcut}</span>}
        </div>
      )}
    </div>
  );

  const sidebarVariants = {
    open: { x: 0, opacity: 1 },
    closed: { x: '-100%', opacity: 0 },
    collapsed: { width: '80px' },
    expanded: { width: '280px' }
  };

  // Mobile sidebar with overlay
  if (isMobile) {
    return (
      <>
        {/* Mobile menu button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleSidebar}
          className="fixed top-3 left-3 z-[100] p-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg shadow-lg"
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <HiMenu className="w-5 h-5" />
        </motion.button>

        {/* Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={toggleSidebar}
              className="fixed inset-0 bg-black z-[90]"
              aria-hidden="true"
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={sidebarRef}
              initial="closed"
              animate="open"
              exit="closed"
              variants={sidebarVariants}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-72 bg-gradient-to-b from-gray-800 to-gray-900 shadow-2xl z-[95] overflow-y-auto"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
            >
              {/* Header with close button */}
              <div className={`sticky top-0 bg-gradient-to-b from-gray-800 to-gray-900 z-10 border-b border-gray-700 transition-shadow ${scrolled ? 'shadow-lg' : ''}`}>
                <div className="p-6 flex justify-between items-center">
                  <div>
                    <h2 className="text-white text-xl font-bold">Super Admin</h2>
                    <p className="text-gray-400 text-sm mt-1">v2.0.0</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleSidebar}
                    className="text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                    aria-label="Close menu"
                  >
                    <HiX className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Current page indicator */}
              <div className="mx-4 my-3 p-2 bg-gray-700/50 rounded-lg">
                <p className="text-xs text-gray-300">Current page</p>
                <p className="text-sm text-white font-medium">{getActiveItem() || 'Dashboard'}</p>
              </div>

              {/* Navigation */}
              <nav className="mt-2 px-3">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <NavLink
                      to={item.path}
                      onClick={toggleSidebar}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-3.5 text-white rounded-lg transition-all mb-1 ${
                          isActive
                            ? 'bg-gray-700 shadow-lg border-l-4 border-gray-400'
                            : 'hover:bg-gray-700/50'
                        }`
                      }
                      aria-current={location.pathname === item.path ? 'page' : undefined}
                    >
                      <item.icon className={`w-5 h-5 mr-3 ${location.pathname === item.path ? 'text-white' : 'text-gray-300'}`} />
                      <span className="text-sm flex-1">{item.label}</span>
                      {location.pathname === item.path && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-1.5 h-1.5 bg-white rounded-full"
                        />
                      )}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>

              {/* Bottom section */}
              <div className="sticky bottom-0 bg-gradient-to-b from-gray-800 to-gray-900 border-t border-gray-700 mt-auto">
                <div className="p-3 space-y-1">
                 

                  <motion.button
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      handleLogout();
                      toggleSidebar();
                    }}
                    className="w-full flex items-center px-4 py-3 text-white hover:bg-red-500/20 rounded-lg transition-all mt-2"
                    aria-label="Logout"
                  >
                    <HiOutlineLogout className="w-5 h-5 mr-3 text-red-400" />
                    <span className="text-sm">Logout</span>
                  </motion.button>

                  {/* Version info */}
                  <div className="pt-4 mt-2 text-center">
                    <p className="text-xs text-gray-500">© 2024 Super Admin</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Desktop sidebar with collapse option
  return (
    <>
      <motion.div
        ref={sidebarRef}
        animate={isCollapsed ? "collapsed" : "expanded"}
        variants={sidebarVariants}
        transition={{ type: 'spring', damping: 20, stiffness: 200 }}
        className="fixed left-0 top-0 bottom-0 bg-gradient-to-b from-gray-800 to-gray-900 shadow-2xl z-30 overflow-hidden"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Collapse toggle button with tooltip */}
        <Tooltip label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} shortcut="Alt+B">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute -right-3 top-10 bg-gray-700 text-white p-2 rounded-full shadow-lg hover:bg-gray-600 z-20 transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <HiChevronRight className="w-3 h-3" />
            ) : (
              <HiChevronLeft className="w-3 h-3" />
            )}
          </motion.button>
        </Tooltip>

        {/* Sidebar content */}
        <div className="h-full flex flex-col">
          {/* Header - Fixed at top */}
          <div className={`sticky top-0 bg-gradient-to-b from-gray-800 to-gray-900 z-10 border-b border-gray-700 transition-shadow ${scrolled ? 'shadow-lg' : ''}`}>
            <div className="p-6">
              <div className="flex items-center">
                <motion.div
                  whileHover={{ rotate: 10 }}
                  className="bg-gray-700 p-2 rounded-lg flex-shrink-0"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </motion.div>
                
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="ml-3"
                    >
                      <h2 className="text-white text-lg font-bold">Super Admin</h2>
                      <p className="text-gray-400 text-xs mt-0.5">v2.0.0</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {isCollapsed && (
                  <h2 className="text-white text-lg font-bold ml-2">SA</h2>
                )}
              </div>
            </div>
          </div>

          {/* Navigation - Scrollable area */}
          <div 
            className="flex-1 overflow-y-auto py-3 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent"
            onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 10)}
          >
            <nav className="px-3 space-y-1">
              {menuItems.map((item, index) => (
                <Tooltip key={item.path} label={item.label} shortcut={item.shortcut}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-3 text-white rounded-lg transition-all group ${
                        isActive
                          ? 'bg-gray-700 shadow-lg border-l-4 border-gray-400'
                          : 'hover:bg-gray-700/50'
                      } ${isCollapsed ? 'justify-center' : ''}`
                    }
                    onMouseEnter={() => setHoveredItem(item.path)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <item.icon className={`w-5 h-5 ${!isCollapsed && 'mr-3'} transition-transform group-hover:scale-110 ${
                      location.pathname === item.path ? 'text-white' : 'text-gray-300'
                    }`} />
                    
                    {!isCollapsed && (
                      <>
                        <span className="text-sm flex-1">{item.label}</span>
                        <span className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                          Alt+{item.shortcut}
                        </span>
                      </>
                    )}
                  </NavLink>
                </Tooltip>
              ))}
            </nav>
          </div>

          {/* Bottom section with logout */}
          <div className="sticky bottom-0 bg-gradient-to-b from-gray-800 to-gray-900 border-t border-gray-700">
            <div className="p-3 space-y-1">
             

              <Tooltip label="Logout">
                <motion.button
                  whileHover={{ x: isCollapsed ? 0 : 5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleLogout}
                  className={`w-full flex items-center px-4 py-3 text-white hover:bg-red-500/20 rounded-lg transition-all ${
                    isCollapsed ? 'justify-center' : ''
                  }`}
                  aria-label="Logout"
                >
                  <HiOutlineLogout className={`w-5 h-5 ${!isCollapsed && 'mr-3'} text-red-400`} />
                  {!isCollapsed && <span className="text-sm">Logout</span>}
                </motion.button>
              </Tooltip>

              {/* Version info - only when expanded */}
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="pt-4 mt-2 text-center"
                >
                  <p className="text-xs text-gray-500">© 2024 Super Admin</p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Spacer div to push main content */}
      <div 
        style={{ width: isCollapsed ? 80 : 280 }} 
        className="flex-shrink-0 transition-all duration-300"
      />
    </>
  );
};

export default Sidebar;